import { createTheme } from "@mui/material/styles";

/**
 * BXI Admin – Ultra-clean minimal design system
 *
 * Inspired by Google Antigravity: generous whitespace, near-monochrome palette,
 * pill-shaped primary buttons, subtle borders, clean typography.
 *
 * Font     : Inter (400 / 500 / 600)
 * Primary  : #000000 (black for CTAs and text) — accent #2563eb only for links/selections
 * Radius   : 12 (cards), 24 (pill buttons), 10 (inputs)
 * Surfaces : flat white, hairline borders, zero heavy shadows
 */

const shared = {
  typography: {
    fontFamily: '"Google Sans Flex", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeightLight: 400,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h3: { fontWeight: 600, fontSize: "2rem", letterSpacing: "-0.02em", lineHeight: 1.2 },
    h4: { fontWeight: 600, fontSize: "1.5rem", letterSpacing: "-0.015em", lineHeight: 1.3 },
    h5: { fontWeight: 600, fontSize: "1.25rem", letterSpacing: "-0.01em", lineHeight: 1.35 },
    h6: { fontWeight: 600, fontSize: "1rem", letterSpacing: "-0.005em", lineHeight: 1.4 },
    subtitle1: { fontWeight: 500, fontSize: "0.9375rem", lineHeight: 1.5 },
    subtitle2: { fontWeight: 500, fontSize: "0.8125rem", lineHeight: 1.5 },
    body1: { fontWeight: 400, fontSize: "0.9375rem", lineHeight: 1.6 },
    body2: { fontWeight: 400, fontSize: "0.8125rem", lineHeight: 1.6 },
    caption: { fontWeight: 400, fontSize: "0.75rem", lineHeight: 1.5 },
    button: { fontWeight: 500, fontSize: "0.875rem", letterSpacing: "0" },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollBehavior: "smooth",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: false },
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 24,
          fontWeight: 500,
          padding: "8px 24px",
          transition: "all 0.15s ease",
        },
        sizeSmall: {
          padding: "6px 16px",
          fontSize: "0.8125rem",
        },
        sizeLarge: {
          padding: "12px 32px",
          fontSize: "0.9375rem",
        },
        containedPrimary: {
          "&:hover": {
            transform: "translateY(-1px)",
          },
        },
        outlinedPrimary: {
          borderWidth: "1.5px",
          "&:hover": {
            borderWidth: "1.5px",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "none",
          border: "1px solid",
          transition: "border-color 0.15s ease",
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "none",
          border: "1px solid",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontSize: "0.9375rem",
          transition: "border-color 0.15s ease",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1.5,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputLabel-root": {
            fontSize: "0.9375rem",
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: "1px solid",
          boxShadow: "none",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            fontWeight: 600,
            fontSize: "0.75rem",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            borderBottom: "1px solid",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          padding: "14px 20px",
          borderBottom: "1px solid",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: "0.75rem",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: "0.75rem",
          fontWeight: 500,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "none",
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
    },
  },
};

/* ─── Light Theme ─── */
export const lightTheme = createTheme({
  ...shared,
  palette: {
    mode: "light",
    primary: {
      main: "#000000",
      light: "#333333",
      dark: "#000000",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#5f6368",
      light: "#9aa0a6",
      dark: "#3c4043",
    },
    info: {
      main: "#2563eb",
      light: "#3b82f6",
      dark: "#1d4ed8",
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
    text: {
      primary: "#000000",
      secondary: "#5f6368",
      disabled: "#9aa0a6",
    },
    divider: "#e8eaed",
    action: {
      hover: "rgba(26, 26, 26, 0.04)",
      selected: "rgba(26, 26, 26, 0.08)",
      focus: "rgba(26, 26, 26, 0.12)",
    },
  },
  components: {
    ...shared.components,
    MuiCard: {
      styleOverrides: {
        root: {
          ...shared.components.MuiCard.styleOverrides.root,
          borderColor: "#e8eaed",
          "&:hover": {
            borderColor: "#dadce0",
          },
        },
      },
    },
    MuiPaper: {
      ...shared.components.MuiPaper,
      styleOverrides: {
        root: {
          ...shared.components.MuiPaper.styleOverrides.root,
          borderColor: "#e8eaed",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        ...shared.components.MuiOutlinedInput.styleOverrides,
        root: {
          ...shared.components.MuiOutlinedInput.styleOverrides.root,
          backgroundColor: "#f8f9fa",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#dadce0",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000000",
            borderWidth: 1.5,
          },
        },
        notchedOutline: {
          borderColor: "#e8eaed",
          transition: "border-color 0.15s ease",
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          ...shared.components.MuiTableContainer.styleOverrides.root,
          borderColor: "#e8eaed",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          ...shared.components.MuiTableHead.styleOverrides.root,
          backgroundColor: "#f8f9fa",
          "& .MuiTableCell-head": {
            ...shared.components.MuiTableHead.styleOverrides.root["& .MuiTableCell-head"],
            color: "#5f6368",
            borderBottomColor: "#e8eaed",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          ...shared.components.MuiTableCell.styleOverrides.root,
          borderBottomColor: "#f1f3f4",
        },
      },
    },
  },
});

/* ─── Dark Theme ─── */
export const darkTheme = createTheme({
  ...shared,
  palette: {
    mode: "dark",
    primary: {
      main: "#e8eaed",
      light: "#ffffff",
      dark: "#dadce0",
      contrastText: "#000000",
    },
    secondary: {
      main: "#9aa0a6",
      light: "#bdc1c6",
      dark: "#5f6368",
    },
    info: {
      main: "#8ab4f8",
      light: "#aecbfa",
      dark: "#669df6",
    },
    background: {
      default: "#1a1a1a",
      paper: "#2d2d2d",
    },
    text: {
      primary: "#e8eaed",
      secondary: "#9aa0a6",
      disabled: "#5f6368",
    },
    divider: "#3c4043",
    action: {
      hover: "rgba(232, 234, 237, 0.06)",
      selected: "rgba(232, 234, 237, 0.10)",
      focus: "rgba(232, 234, 237, 0.14)",
    },
  },
  components: {
    ...shared.components,
    MuiCard: {
      styleOverrides: {
        root: {
          ...shared.components.MuiCard.styleOverrides.root,
          borderColor: "#3c4043",
          "&:hover": {
            borderColor: "#5f6368",
          },
        },
      },
    },
    MuiPaper: {
      ...shared.components.MuiPaper,
      styleOverrides: {
        root: {
          ...shared.components.MuiPaper.styleOverrides.root,
          borderColor: "#3c4043",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        ...shared.components.MuiOutlinedInput.styleOverrides,
        root: {
          ...shared.components.MuiOutlinedInput.styleOverrides.root,
          backgroundColor: "#3c4043",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#5f6368",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#e8eaed",
            borderWidth: 1.5,
          },
        },
        notchedOutline: {
          borderColor: "#3c4043",
          transition: "border-color 0.15s ease",
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          ...shared.components.MuiTableContainer.styleOverrides.root,
          borderColor: "#3c4043",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          ...shared.components.MuiTableHead.styleOverrides.root,
          backgroundColor: "#2d2d2d",
          "& .MuiTableCell-head": {
            ...shared.components.MuiTableHead.styleOverrides.root["& .MuiTableCell-head"],
            color: "#9aa0a6",
            borderBottomColor: "#3c4043",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          ...shared.components.MuiTableCell.styleOverrides.root,
          borderBottomColor: "#3c4043",
        },
      },
    },
  },
});
